class ImageComparisonSlider extends HTMLElement {
  constructor() {
    super();
    
    this.container = this.querySelector('.image-comparison__container');
    this.beforeImage = this.querySelector('.image-comparison__image--before');
    this.afterImage = this.querySelector('.image-comparison__image--after');
    this.handle = this.querySelector('.image-comparison__handle');
    this.slider = this.querySelector('.image-comparison__slider');
    
    this.isDragging = false;
    this.hasAnimated = false;
    this.observer = null;
    this.rafId = null; // For smooth mobile touch
    
    if (this.slider && this.beforeImage && this.handle) {
      this.init();
    }
  }
  
  init() {
    // Set initial position at 0 (will animate to 50)
    this.updatePosition(0, false);
    
    // Mouse events
    this.slider.addEventListener('input', (e) => this.onSliderInput(e));
    this.slider.addEventListener('mousedown', () => this.onDragStart());
    
    document.addEventListener('mouseup', () => this.onDragEnd());
    
    // Keyboard navigation
    this.slider.addEventListener('keydown', (e) => this.onKeyDown(e));
    
    // Improved mobile touch events
    this.setupTouchEvents();
    
    // Prevent context menu on long press
    this.slider.addEventListener('contextmenu', (e) => e.preventDefault());
    
    // Intersection Observer for scroll animation
    this.setupScrollAnimation();
  }
  
  setupScrollAnimation() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3 // Trigger when 30% visible
    };
    
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasAnimated) {
          this.animateSlider();
          this.hasAnimated = true;
        }
      });
    }, options);
    
    this.observer.observe(this.container);
  }
  
  animateSlider() {
    // Animate from 0 to 50 over 1 second
    const duration = 1000;
    const startValue = 0;
    const endValue = 50;
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easeOut;
      
      this.slider.value = currentValue;
      this.updatePosition(currentValue, true);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }
  
  setupTouchEvents() {
    const handleTouchStart = (e) => {
      this.onDragStart();
      
      // Cancel any ongoing animation
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }

      // Prevent scrolling while dragging
      if (e.cancelable) {
        e.preventDefault();
      }
    };

    const handleTouchMove = (e) => {
      if (!this.isDragging) return;
      
      // Prevent page scrolling
      if (e.cancelable) {
        e.preventDefault();
      }
      
      const touch = e.touches[0];
      const rect = this.container.getBoundingClientRect();
      
      // Calculate position relative to container
      const x = touch.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      // Use requestAnimationFrame for smooth 60fps updates
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
      
      this.rafId = requestAnimationFrame(() => {
        this.slider.value = percentage;
        this.updatePosition(percentage, false);
      });
    };

    const handleTouchEnd = (e) => {
      this.onDragEnd();
      
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    };

    // Add touch event listeners to the slider
    this.slider.addEventListener('touchstart', handleTouchStart, { passive: false });
    this.slider.addEventListener('touchmove', handleTouchMove, { passive: false });
    this.slider.addEventListener('touchend', handleTouchEnd);
    this.slider.addEventListener('touchcancel', handleTouchEnd);

    // Also add to container for better touch target
    this.container.addEventListener('touchstart', handleTouchStart, { passive: false });
    this.container.addEventListener('touchmove', handleTouchMove, { passive: false });
    this.container.addEventListener('touchend', handleTouchEnd);
    this.container.addEventListener('touchcancel', handleTouchEnd);
  }
  
  onSliderInput(e) {
    const value = parseFloat(e.target.value);
    this.updatePosition(value, true);
  }
  
  onDragStart() {
    this.isDragging = true;
    if (this.container) {
      this.container.classList.add('is-dragging');
    }
  }
  
  onDragEnd() {
    if (this.isDragging) {
      this.isDragging = false;
      if (this.container) {
        this.container.classList.remove('is-dragging');
      }
    }
  }
  
  onKeyDown(e) {
    const currentValue = parseFloat(this.slider.value);
    let newValue = currentValue;
    
    switch(e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newValue = Math.max(0, currentValue - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        newValue = Math.min(100, currentValue + 1);
        break;
      case 'Home':
        e.preventDefault();
        newValue = 0;
        break;
      case 'End':
        e.preventDefault();
        newValue = 100;
        break;
    }
    
    if (newValue !== currentValue) {
      this.slider.value = newValue;
      this.updatePosition(newValue, true);
    }
  }
  
  updatePosition(value, addTransition = false) {
    const percentage = value;
    
    // Add transition class during animation
    if (addTransition) {
      this.beforeImage.style.transition = 'clip-path 0.05s ease';
      this.handle.style.transition = 'left 0.05s ease';
    } else {
      this.beforeImage.style.transition = 'none';
      this.handle.style.transition = 'none';
    }
    
    // Update clip-path on before image (left side)
    // Before image is clipped from right side
    this.beforeImage.style.clipPath = `inset(0 ${100 - percentage}% 0 0)`;
    
    // Update handle position
    this.handle.style.left = `${percentage}%`;
  }
  
  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  }
}

// Register custom element
if (!customElements.get('image-comparison-slider')) {
  customElements.define('image-comparison-slider', ImageComparisonSlider);
}

// Initialize all sliders
function initImageComparisonSliders() {
  const sliders = document.querySelectorAll('image-comparison-slider');
  sliders.forEach(slider => {
    if (!slider.initialized) {
      slider.initialized = true;
    }
  });
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initImageComparisonSliders);
} else {
  initImageComparisonSliders();
}

// Shopify section events (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const slider = event.target.querySelector('image-comparison-slider');
    if (slider && !slider.initialized) {
      slider.initialized = true;
    }
  });
  
  document.addEventListener('shopify:section:unload', function(event) {
    const slider = event.target.querySelector('image-comparison-slider');
    if (slider) {
      slider.initialized = false;
    }
  });
}